import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { FilterTab } from "components/tab-filter/tab-filter.props";
import { Filter } from "libs/models/filter";
import { PaginatedList } from "libs/models/paginated-list";
import {
  PointOfSaleModel,
  RequestPointOfSaleModel,
  UpdatePointOfSaleModel,
} from "libs/models/point-of-sale-model";
import {
  addPointOfSaleService,
  changePointOfSaleAPIKeyService,
  getPointOfSaleByIdService,
  getPointOfSaleService,
  updatePointOfSaleService,
} from "services/crosspay-service";
import { AsyncAppThunk } from "store/application-state";
import { getFilterQuery } from "utils/filter";
import { addSnack, showLoaderWhile } from "./ui";
import { CustomizedBack } from "store/navigation-wrapper";
import { validateEditPointSaleState } from "pages/EditPointOfSale/edit-point-of-sale.policy";
import { ValidateAddPointOfSaleState } from "pages/AddPointOfSale/add-point-of-sale.policy";
import translate from "utils/translations";
import { GENERATED_API_KEYS_KEY } from "static/static-values";

export interface PointOfSaleStore {
  isValidating: boolean;
  ApiKey: string | undefined;
  pointOfSalesList: PaginatedList<PointOfSaleModel>;
  pointOfSaleDetails: PointOfSaleModel;
  pointOfSaleRequest: RequestPointOfSaleModel;
  pointOfSaleUpdate: UpdatePointOfSaleModel;
  filter: Filter;
  searchingValue: string | undefined;
  tabFilterValue: FilterTab | undefined;
  isDialogOpen: boolean;
  generatedAPIKeys: number;
}

const initialState: PointOfSaleStore = {
  isValidating: false,
  isDialogOpen: false,
  ApiKey: undefined,
  pointOfSaleDetails: {} as PointOfSaleModel,
  pointOfSaleUpdate: {} as UpdatePointOfSaleModel,
  pointOfSaleRequest: {} as RequestPointOfSaleModel,
  pointOfSalesList: { list: [], pageSize: 10, pageIndex: 0, totalCount: 0 },
  filter: {
    orderBy: { key: "Name", sort: "asc" },
  },
  searchingValue: undefined,
  tabFilterValue: undefined,
  generatedAPIKeys: 0,
};

const slice = createSlice({
  name: "pointofsale",
  initialState,
  reducers: {
    setIsValidating(state: PointOfSaleStore, action: PayloadAction<boolean>) {
      state.isValidating = action.payload;
    },
    setApiKey(state: PointOfSaleStore, action: PayloadAction<string>) {
      state.ApiKey = action.payload;
    },
    setPointOfSalesList(
      state: PointOfSaleStore,
      action: PayloadAction<PaginatedList<PointOfSaleModel>>,
    ) {
      state.pointOfSalesList = action.payload;
    },
    setPOSPage: (state: PointOfSaleStore, action: PayloadAction<number>) => {
      state.pointOfSalesList.pageIndex = action.payload;
    },
    setPointOfSaleDetails(
      state: PointOfSaleStore,
      action: PayloadAction<PointOfSaleModel>,
    ) {
      state.pointOfSaleDetails = action.payload;
    },
    setPointOfSaleDetailsProps(
      state: PointOfSaleStore,
      action: PayloadAction<{ key: string; value?: any }>,
    ) {
      const { key, value } = action.payload;

      state.pointOfSaleDetails = {
        ...state.pointOfSaleDetails,
        [key]: value!,
      };
    },
    setPointOfSaleRequest: (
      state: PointOfSaleStore,
      action: PayloadAction<{ key: string; value?: any }>,
    ) => {
      const { key, value } = action.payload;
      return {
        ...state,
        pointOfSaleRequest: {
          ...state.pointOfSaleRequest,
          [key]: value!,
        },
      };
    },
    setEqualFilter: (
      state: PointOfSaleStore,
      action: PayloadAction<{ key: string; value?: any }>,
    ) => {
      const { key, value } = action.payload;
      if (value === null) {
        delete state.filter[key];
        return state;
      } else {
        state.filter = { ...state.filter, [key]: value! };
      }
      return state;
    },
    setClearForms: (state: PointOfSaleStore) => {
      state.pointOfSaleRequest = {} as RequestPointOfSaleModel;
      state.pointOfSaleUpdate = {} as UpdatePointOfSaleModel;
      state.pointOfSaleDetails = {} as PointOfSaleModel;
      state.ApiKey = undefined;
      state.isValidating = false;
    },
    clearFilters: (state: PointOfSaleStore) => {
      state.filter = initialState.filter;
      state.searchingValue = undefined;
    },
    setGeneratingAPIKey: (
      state: PointOfSaleStore,
      action: PayloadAction<number>,
    ) => {
      state.generatedAPIKeys = action.payload;
      localStorage.setItem(GENERATED_API_KEYS_KEY, action.payload.toString());
    },
  },
});

export const {
  setPointOfSalesList,
  setPointOfSaleRequest,
  setPointOfSaleDetails,
  setPointOfSaleDetailsProps,
  setIsValidating,
  setApiKey,
  setClearForms,
  setPOSPage,
  setEqualFilter,
  clearFilters,
  setGeneratingAPIKey,
} = slice.actions;
export const reducer = slice.reducer;

export const getPointOfSaleList =
  (): AsyncAppThunk => async (dispatch, store) => {
    dispatch(setClearForms());
    dispatch(
      showLoaderWhile(async () => {
        var pointOfSalePaging = store().pointofsales.pointOfSalesList;
        var queryOptions = getFilterQuery(
          store().pointofsales.filter,
          pointOfSalePaging.pageIndex * pointOfSalePaging.pageSize,
          pointOfSalePaging.pageSize,
        );
        const apiResponse = await getPointOfSaleService(queryOptions);
        if (
          apiResponse &&
          Object.keys(apiResponse).length > 0 &&
          apiResponse.result !== null
        ) {
          dispatch(setPointOfSalesList(apiResponse.result));
        }
      }),
    );
  };
export const getPointOfSaleById =
  (id: string): AsyncAppThunk =>
  async (dispatch, store) => {
    dispatch(
      showLoaderWhile(async () => {
        const apiResponse = await getPointOfSaleByIdService(id);
        if (
          apiResponse &&
          Object.keys(apiResponse).length > 0 &&
          apiResponse.result !== null
        ) {
          dispatch(setPointOfSaleDetails(apiResponse.result));
        }
      }),
    );
  };
export const discardChanges = (): AsyncAppThunk => async (dispatch, store) => {
  dispatch(setClearForms());
  CustomizedBack();
};
export const addPointOfSale = (): AsyncAppThunk => async (dispatch, store) => {
  var pointOfSaleRequest = store().pointofsales.pointOfSaleRequest;
  dispatch(setIsValidating(true));
  if (
    Object.keys(ValidateAddPointOfSaleState(store().pointofsales)).length === 0
  ) {
    dispatch(
      showLoaderWhile(async () => {
        var apiResponse = await addPointOfSaleService(pointOfSaleRequest);
        if (
          apiResponse &&
          Object.keys(apiResponse).length > 0 &&
          apiResponse.result !== null
        ) {
          dispatch(
            addSnack({
              message: translate(
                "PointOfSale_Added_Successfully",
                "Point of Sale Added Successfully",
              ),
            }),
          );
          dispatch(setApiKey(apiResponse.result));
          dispatch(setIsValidating(false));
        }
      }),
    );
  }
};

export const updatePointOfSale =
  (): AsyncAppThunk => async (dispatch, store) => {
    var posDetails = store().pointofsales.pointOfSaleDetails;
    const updateRequest = {
      Id: posDetails.id,
      name: posDetails.name,
    } as UpdatePointOfSaleModel;
    dispatch(setIsValidating(true));
    if (
      Object.keys(validateEditPointSaleState(store().pointofsales)).length === 0
    ) {
      dispatch(
        showLoaderWhile(async () => {
          const apiResponse = await updatePointOfSaleService(updateRequest);
          if (
            apiResponse &&
            Object.keys(apiResponse).length > 0 &&
            apiResponse.result !== null
          ) {
            dispatch(
              addSnack({
                message: translate(
                  "PointOfSale_Edited_Successfully",
                  "Point of Sale Edited Successfully",
                ),
              }),
            );
            dispatch(setIsValidating(false));
            CustomizedBack();
          }
        }),
      );
    }
  };
export const changePointOfSaleAPIKey =
  (id: string): AsyncAppThunk =>
  async (dispatch, store) => {
    const generatedAPIKeys = localStorage.getItem(GENERATED_API_KEYS_KEY);

    if (generatedAPIKeys && parseInt(generatedAPIKeys) > 0) {
      dispatch(
        addSnack({
          message: translate(
            "Regenerate_Limit_Text",
            "You can regenerate the API key once every 5 minutes",
          ),
        }),
      );
      setTimeout(() => {
        dispatch(setGeneratingAPIKey(0));
      }, 300000);
      return;
    }
    dispatch(setGeneratingAPIKey(1));
    dispatch(
      showLoaderWhile(async () => {
        const apiResponse = await changePointOfSaleAPIKeyService(id);
        if (
          apiResponse &&
          Object.keys(apiResponse).length > 0 &&
          apiResponse.result !== null
        ) {
          dispatch(setApiKey(apiResponse.result));
        }
      }),
    );
  };
